import {
  ArticleBullets,
  ArticleImage,
  ArticleLayout,
  ArticleParagraph,
  ArticleSection,
  QuickVerdict,
} from '../../../components/marketing/ArticleLayout';
import {
  ARTICLE_IMAGES,
  ARTICLE_SUMMARY_BY_SLUG,
} from '../../../lib/content/articles';

const article = ARTICLE_SUMMARY_BY_SLUG['planning-for-kids-college-and-family-life-events'];

export default function PlanningForKidsAndFamilyEventsPage() {
  return (
    <ArticleLayout
      article={article}
      relatedSlugs={['build-a-shared-plan-with-your-partner', 'what-if-i-retire-earlier']}
    >
      <QuickVerdict
        title="Quick take"
        items={[
          'A household plan changes meaningfully when kids, education decisions, and future family events are brought into view.',
          'Per Diem treats dependents and life events as planning inputs, not just notes sitting outside the model.',
          'The result is a version of FIRE planning that respects the shape of family life instead of abstracting it away.',
        ]}
      />

      <ArticleSection title="Family planning is where generic financial models often break down">
        <ArticleParagraph>
          It is easy to produce a clean retirement projection when the household is treated like a
          generic saver with a single timeline. Things get much messier when the real commitments of
          family life show up.
        </ArticleParagraph>
        <ArticleParagraph>
          Children, education costs, caregiving needs, and major household changes can all reshape
          what financial freedom means. A plan that does not represent those realities may still
          look elegant, but it is not especially helpful.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.addPartner}
        alt="Per Diem household planning"
        caption="A household plan becomes more useful when the people and commitments behind the numbers are represented clearly."
      />

      <ArticleSection title="Why kids and education cannot be an afterthought">
        <ArticleBullets
          items={[
            'Children change monthly expenses and long-term savings priorities.',
            'Education goals can compete with other freedom goals if they are invisible in the plan.',
            'Recurring life events create future obligations that should be modeled earlier, not remembered later.',
            'The household needs a way to discuss tradeoffs in one shared system.',
          ]}
        />
      </ArticleSection>

      <ArticleSection title="Life events are not noise in the model">
        <ArticleParagraph>
          A strong household plan should be able to absorb events such as a child starting school,
          recurring education expenses, one partner stepping back from work, or other meaningful
          transitions. These are not minor details. They shape the timeline and the household’s
          sense of financial slack.
        </ArticleParagraph>
        <ArticleParagraph>
          Per Diem brings those events into the same planning context as income, budgeting, and
          assets so the household can see what changes when life changes.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileScenarios}
        alt="Per Diem mobile scenario planning screen"
        caption="Family life changes the plan through scenarios: future expenses, work changes, education goals, and the tradeoffs around them."
        variant="phone"
      />

      <ArticleSection title="The point is not to make family life feel transactional">
        <ArticleParagraph>
          Bringing kids and life events into the model is not about flattening family decisions into
          line items. It is about giving those decisions the respect they deserve in the financial
          system the household uses to think clearly.
        </ArticleParagraph>
        <ArticleParagraph>
          That is how a planning tool becomes more human instead of less.
        </ArticleParagraph>
      </ArticleSection>
    </ArticleLayout>
  );
}
